// src/pages/HomePage/components/AttendSection.jsx
import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Navigation, Search, X, Calendar, Users } from "lucide-react";

const LOCATION_KEY = "kleido-user-location";

console.log({ MapPin, Navigation, Search, X, Calendar, Users });

const A = {
  wrap: { width: "100%" },
  locBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    padding: "14px 18px",
    marginBottom: "28px",
    borderRadius: "12px",
    border: "1px solid var(--border-color, rgb(52,51,51))",
    background: "rgba(255,255,255,0.03)",
  },
  btn: (primary = false) => ({
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 14px",
    borderRadius: "8px",
    border: "1px solid var(--border-color, rgb(52,51,51))",
    background: primary ? "rgba(255,255,255,0.12)" : "transparent",
    color: primary ? "white" : "rgba(255,255,255,0.8)",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 500,
    whiteSpace: "nowrap",
    transition: "background 0.2s, color 0.2s",
  }),
  input: {
    flex: 1,
    minWidth: "180px",
    padding: "9px 12px",
    borderRadius: "8px",
    border: "1px solid var(--border-color, rgb(52,51,51))",
    background: "rgba(255,255,255,0.05)",
    color: "white",
    fontSize: "13px",
    outline: "none",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "18px",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    borderRadius: "14px",
    overflow: "hidden",
    border: "1px solid var(--border-color, rgb(52,51,51))",
    background: "rgba(36,36,45,0.25)",
    cursor: "pointer",
    transition: "transform 0.2s, border-color 0.2s",
  },
  cardMedia: {
    position: "relative",
    aspectRatio: "16 / 9",
    background: "rgba(255,255,255,0.04)",
    overflow: "hidden",
  },
  chip: {
    position: "absolute",
    top: "10px",
    left: "10px",
    padding: "3px 9px",
    borderRadius: "999px",
    background: "rgba(0,0,0,0.65)",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "rgba(255,255,255,0.85)",
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    backdropFilter: "blur(6px)",
  },
  cardBody: { padding: "14px 16px 16px" },
  cardTitle: {
    margin: "0 0 6px",
    fontSize: "15px",
    fontWeight: 600,
    color: "white",
    lineHeight: 1.3,
  },
  cardMeta: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    color: "rgba(255,255,255,0.45)",
    marginBottom: "4px",
  },
  empty: {
    padding: "60px 20px",
    textAlign: "center",
    color: "rgba(255,255,255,0.4)",
    fontSize: "14px",
  },
};

const AttendSection = ({ userid, onOpenEvent }) => {
  const [location, setLocation]   = useState(null);   // { label, lat, lng, source }
  const [manual, setManual]       = useState("");
  const [editing, setEditing]     = useState(false);
  const [locating, setLocating]   = useState(false);
  const [locError, setLocError]   = useState("");
  const [events, setEvents]       = useState([]);
  const [loading, setLoading]     = useState(false);

  // Restore a previously chosen location
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCATION_KEY);
      if (raw) setLocation(JSON.parse(raw));
      else setEditing(true);
    } catch {
      setEditing(true);
    }
  }, []);

  const saveLocation = (loc) => {
    setLocation(loc);
    setEditing(false);
    setLocError("");
    try { localStorage.setItem(LOCATION_KEY, JSON.stringify(loc)); } catch { /* ignore */ }
  };

  // ---- Browser geolocation ----
  const detectLocation = () => {
    if (!navigator.geolocation) {
      setLocError("Your browser doesn't support location detection. Enter a city instead.");
      return;
    }
    setLocating(true);
    setLocError("");

    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const { latitude: lat, longitude: lng } = coords;
        let label = `${lat.toFixed(3)}, ${lng.toFixed(3)}`;

        // TODO: reverse-geocode for a readable city name.
        // const res = await fetch(`/api/reverse-geocode?lat=${lat}&lng=${lng}`);
        // label = (await res.json()).city;

        saveLocation({ label, lat, lng, source: "gps" });
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        setLocError(
          err.code === err.PERMISSION_DENIED
            ? "Location access was blocked. Enter your city below instead."
            : "Couldn't get your location. Enter your city below instead."
        );
        setEditing(true);
      },
      { timeout: 10000, maximumAge: 5 * 60 * 1000 }
    );
  };

  const submitManual = () => {
    const label = manual.trim();
    if (!label) return;
    saveLocation({ label, lat: null, lng: null, source: "manual" });
    setManual("");
  };

  // ---- Fetch events for the chosen location ----
  const loadEvents = useCallback(async () => {
    if (!location) return;
    setLoading(true);
    try {
      // TODO: replace with your real endpoint.
      // const params = new URLSearchParams({ userid, location: location.label });
      // if (location.lat) { params.set("lat", location.lat); params.set("lng", location.lng); }
      // const res = await fetch(`/api/events/nearby?${params}`);
      // setEvents(await res.json());
      setEvents([]);
    } catch (err) {
      console.error("❌ [Attend] Failed to load events:", err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [location, userid]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  return (
    <div style={A.wrap}>
      {/* ── Location bar ── */}
      <div style={A.locBar}>
        {location && !editing ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: "9px", minWidth: 0 }}>
              <MapPin size={15} style={{ flexShrink: 0, color: "rgba(255,255,255,0.5)" }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                  {location.label}
                </div>
                <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.35)" }}>
                  {location.source === "gps" ? "Detected automatically" : "Set by you"}
                </div>
              </div>
            </div>
            <button style={A.btn()} onClick={() => setEditing(true)}>
              Change
            </button>
          </>
        ) : (
          <div style={{ width: "100%" }}>
            <div style={{
              fontSize: "13px",
              color: "rgba(255,255,255,0.6)",
              marginBottom: "12px",
            }}>
              Where should we look for events?
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <button style={A.btn(true)} onClick={detectLocation} disabled={locating}>
                <Navigation size={14} />
                {locating ? "Locating…" : "Use my location"}
              </button>

              <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.3)" }}>or</span>

              <input
                type="text"
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitManual(); }}
                placeholder="Enter a city…"
                aria-label="Enter your city"
                style={A.input}
              />

              <button style={A.btn()} onClick={submitManual} disabled={!manual.trim()}>
                <Search size={14} />
                Go
              </button>

              {location && (
                <button
                  style={{ ...A.btn(), border: "none" }}
                  onClick={() => setEditing(false)}
                  aria-label="Cancel"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {locError && (
              <div style={{
                marginTop: "10px",
                fontSize: "12px",
                color: "#ff7676",
              }}>
                {locError}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Feed ── */}
      {!location ? (
        <div style={A.empty}>Set a location to see what's on nearby.</div>
      ) : loading ? (
        <div style={A.empty}>Finding events near {location.label}…</div>
      ) : events.length === 0 ? (
        <div style={A.empty}>
          Nothing scheduled near {location.label} yet. Try a nearby city.
        </div>
      ) : (
        <div style={A.grid}>
          <AnimatePresence>
            {events.map((ev, i) => (
              <motion.div
                key={ev.eventid}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                style={A.card}
                onClick={() => onOpenEvent?.(ev)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.22)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.borderColor = "var(--border-color, rgb(52,51,51))";
                }}
              >
                <div style={A.cardMedia}>
                  {ev.media?.[0] && (
                    <img
                      src={ev.media[0]}
                      alt=""
                      loading="lazy"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  )}
                  {ev.distance != null && (
                    <span style={A.chip}>{ev.distance} km away</span>
                  )}
                </div>

                <div style={A.cardBody}>
                  <h4 style={A.cardTitle}>{ev.eventname}</h4>

                  <div style={A.cardMeta}>
                    <Calendar size={12} />
                    {new Date(ev.startdate).toLocaleDateString(undefined, {
                      day: "numeric", month: "short", year: "numeric",
                    })}
                  </div>

                  <div style={A.cardMeta}>
                    <MapPin size={12} />
                    {ev.venue || ev.location}
                  </div>

                  {ev.attendees != null && (
                    <div style={{ ...A.cardMeta, marginBottom: 0 }}>
                      <Users size={12} />
                      {ev.attendees} going
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default AttendSection;